
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  IndustryAndCategoryModel,
  IndustryAndCategoryPageSearchModel,
  IndustryAndCategorySearchModel,
  IndustryAndCategoryAddModel,
  IndustryAndCategoryUpdateModel,
  IdsModel
} from '@/model/product/IndustryAndCategoryModel'
import {
  pageSearchIndustryAndCategory,
  addIndustryAndCategory,
  updateIndustryAndCategory,
  removeIndustryAndCategory,
  batchRemoveIndustryAndCategory,
  getIndustryAndCategory
} from '@/apis/product/industryAndCategory'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'categoryName', label: t('industryAndCategory.categoryName'), placeholder: t('industryAndCategory.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'fatherCategoryId', label: t('industryAndCategory.fatherCategoryId'), placeholder: t('industryAndCategory.fatherCategoryIdP'), component: 'el-input', type: 'number', model: null }
    ]

    const createStruct = [
      { prop: 'categoryName', label: t('industryAndCategory.categoryName'), placeholder: t('industryAndCategory.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'fatherCategoryId', label: t('industryAndCategory.fatherCategoryId'), placeholder: t('industryAndCategory.fatherCategoryIdP'), component: 'el-input', type: 'number', model: null }
    ]

    const updateStruct = [
      { prop: 'categoryName', label: t('industryAndCategory.categoryName'), placeholder: t('industryAndCategory.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'fatherCategoryId', label: t('industryAndCategory.fatherCategoryId'), placeholder: t('industryAndCategory.fatherCategoryIdP'), component: 'el-input', type: 'number', model: null }
    ]

    const industryAndCategoryRole = {
      categoryName: [{ required: true, message: t('industryAndCategory.categoryName').concat(t('common.isRequired')), trigger: 'blur' }],
      fatherCategoryId: [{
        validator: (rule: any, value: any, callback: any) => {
          if (value < 0) {
            callback(new Error(t('industryAndCategory.fatherCategoryId').concat(t('common.minNumber')).concat(' 0')))
          } else {
            callback()
          }
        },
        trigger: 'blur'
      }]
    }

    const tableStruct = [
      { prop: 'id', label: t('industryAndCategory.categoryId'), sortable: 'custom' },
      { prop: 'categoryName', label: t('industryAndCategory.categoryName'), sortable: 'custom' },
      { prop: 'fatherCategoryId', label: t('industryAndCategory.fatherCategoryId'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: IndustryAndCategoryModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: IndustryAndCategoryModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as IndustryAndCategorySearchModel,
      tableData: Array<IndustryAndCategoryModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'industryAndCategory.edit',
        create: 'industryAndCategory.create'
      },
      dialogFormVisible: false,
      industryAndCategoryRole: industryAndCategoryRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as IndustryAndCategoryAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as IndustryAndCategoryUpdateModel,
      editRow: {} as IndustryAndCategoryModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: IndustryAndCategoryPageSearchModel = {
          categoryName: dataMap.searchFormModel.categoryName,
          fatherCategoryId: dataMap.searchFormModel.fatherCategoryId,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchIndustryAndCategory(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: IndustryAndCategoryAddModel = {
          categoryName: dataMap.createModel.categoryName,
          fatherCategoryId: dataMap.createModel.fatherCategoryId
        }
        if (createData.fatherCategoryId != null && createData.fatherCategoryId.toString() !== '') {
          const industryAndCategory = await getIndustryAndCategory(createData.fatherCategoryId)
          if (industryAndCategory?.data.id === undefined) {
            ElMessage({
              message: t('common.noFatherCategoryId'),
              type: 'error',
              duration: 2000
            })
            return
          }
        }
        const result = await addIndustryAndCategory(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: IndustryAndCategoryUpdateModel = {
          id: dataMap.updateModel.id,
          categoryName: dataMap.updateModel.categoryName,
          fatherCategoryId: dataMap.updateModel.fatherCategoryId
        }
        const result = await updateIndustryAndCategory(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeIndustryAndCategory(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveIndustryAndCategory(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.categoryName = dataMap.editRow.categoryName
        dataMap.updateModel.fatherCategoryId = dataMap.editRow.fatherCategoryId === 0 ? null : dataMap.editRow.fatherCategoryId
      },
      createReset() {
        console.log('create reset')
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: IndustryAndCategoryModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.categoryName = row.categoryName
        dataMap.updateModel.fatherCategoryId = row.fatherCategoryId === 0 ? null : dataMap.editRow.fatherCategoryId
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      ...toRefs(dataMap)
    }
  }
})
